import { useState, useEffect } from 'react'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'

interface SettingItem {
  chiave: string
  valore: string | null
  descrizione: string | null
  gruppo: string
  tipo: string
}

type SettingsMap = Record<string, SettingItem[]>

let _cache: Record<string, string | null> | null = null
let _promise: Promise<Record<string, string | null>> | null = null

async function fetchAll(): Promise<Record<string, string | null>> {
  if (_cache) return _cache
  if (_promise) return _promise

  _promise = axiosInstance
    .get<SettingsMap>(API_ENDPOINTS.SETTINGS_PUBLIC)
    .then((res) => {
      const flat: Record<string, string | null> = {}
      for (const items of Object.values(res.data)) {
        for (const s of items) {
          flat[s.chiave] = s.valore
        }
      }
      _cache = flat
      _promise = null
      return flat
    })
    .catch(() => {
      _promise = null
      return {}
    })

  return _promise
}

/** Invalida la cache (utile dopo un salvataggio nella pagina admin) */
export function invalidateIntegrationSettingsCache() {
  _cache = null
  _promise = null
}

/**
 * Hook leggero che carica le impostazioni di integrazione la prima volta
 * e le restituisce come mappa chiave→valore.
 * Usa /settings/public → accessibile a tutti gli utenti autenticati.
 */
export function useIntegrationSettings() {
  const [settings, setSettings] = useState<Record<string, string | null>>(_cache ?? {})
  const [loading, setLoading] = useState(!_cache)

  useEffect(() => {
    if (_cache) {
      setSettings(_cache)
      setLoading(false)
      return
    }
    setLoading(true)
    fetchAll().then((s) => {
      setSettings(s)
      setLoading(false)
    })
  }, [])

  return { settings, loading }
}
